/**
 * Normalise `GET /inboxes/{id}/mailboxes` into a flat mailbox-connection list.
 *
 * That endpoint returns `inbox_mailbox_refs` rows with the connection NESTED
 * under `connection`, and the provider value is `'google'` or `'microsoft'` —
 * `'gmail'` is only ever a scope-bundle name, never a provider. Reading the
 * shape wrong is not a hypothetical: `AdminUnifiedInboxTab` filtered on
 * `m.provider === 'gmail'`, which is wrong twice over and matched nothing, so
 * the Gmail panel read `0 Active` with mailboxes attached.
 *
 * Both readers of this endpoint go through here so the shape is decoded in one
 * place. Each keeps its own filtering and status presentation — this returns the
 * raw union of ref fields and connection fields and derives nothing.
 *
 * @param {object|Array} data The hook's payload (`{ data: [...] }` or an array).
 * @returns {Array<{email: string, provider: string, status: string,
 *   needs_reauth: boolean, connection_id: string, ref_id: string,
 *   reply_policy: string, grant_type?: string, delegated_mailbox?: string}>}
 */
export function connectionsFromRefs(data) {
    const refs = Array.isArray(data) ? data : data?.data;
    if (!Array.isArray(refs)) return [];

    return refs
        .filter(ref => ref?.connection)
        .map(ref => ({
            ...ref.connection,
            ref_id: ref.ref_id,
            reply_policy: ref.reply_policy,
            // `email_address` is the connection's field name; `email` is what
            // every consumer here reads. Both are kept so a caller that already
            // knew the raw name is not broken by going through this helper.
            email: ref.connection.email_address,
        }));
}

/**
 * Does this inbox hold an active connection for `mail`?
 *
 * The provider-neutral answer to "has this mailbox granted access to this
 * inbox". A Microsoft delegated grant lands in `mailbox_tokens`, but a Google
 * one lands in `mailbox_connections` and is surfaced only through the inbox's
 * refs — so a status derived from tokens alone reports a consented Gmail mailbox
 * as `Not Requested`, complete with a button offering to request the access it
 * already has.
 *
 * Returns `'active'`, `'reauth'`, `'revoked'`, or `undefined` when this inbox
 * has no connection for the address at all.
 */
export function connectionStatusFor(mail, connections = []) {
    const needle = String(mail || '')
        .toLowerCase()
        .trim();
    if (!needle) return undefined;

    const list = Array.isArray(connections) ? connections : [];
    const match = list.find(
        c =>
            String(c?.email || c?.email_address || '')
                .toLowerCase()
                .trim() === needle
    );
    if (!match) return undefined;
    // `needs_reauth` outranks `status`: the row stays `active` while the grant
    // itself has stopped working, and the admin needs to be told to re-request.
    if (match.needs_reauth) return 'reauth';
    return match.status;
}
